import {useMemo} from 'react'
import cloneDeep from 'lodash/cloneDeep.js'
import isEqual from 'lodash/isEqual.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../../store.js'
import ConfirmModal from '../../../../common/components/Modal/ConfirmModal.js'
import {
  FEDEX,
  SHIPPER_OPTIONS,
  UPS,
} from '../../../../common/constants/ShipperNames.js'
import {
  addParcel,
  updateLabelConfig,
  makeLabelShipperNameSelector,
  labelConfigSelector,
  orderNumberFromLabelConfigSelector,
  referenceIDFromLabelConfigSelector,
  canAddMoreParcelsSelector,
  selectedShippingRateCostSelector,
  ratesLoadingSelector,
  labelErrorsSelector,
  makeBoxShapeNameSelector,
  createLabelImageDisabledSelector,
  multiboxShipperTypesSelector,
  shipperTypesSelector,
  labelShippingMethodNameSelector,
  getMaxParcelCount,
  packagesSelector,
  VALID_DECLARED_VALUE_SHIPPER_TYPES,
} from '../../../../data/labelInfos/index.js'
import {getRates} from '../../../../data/labelInfos/rateRequest.js'
import Currency, {isTBD} from '../../../../common/components/Currency.js'
import Row from './Row.js'
import Zelect from '../../../../common/components/Zelect.js'
import {showMessageToast} from '../../../Header/Toast/index.js'
import ButtonRefresh from '../../../../common/components/Button/ButtonRefresh.js'
import className from '../../../../common/className.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import {orderLinesSelector} from '../../../../data/orders.js'
import {returnOrderLinesSelector} from '../../../../redux/selectors/data/returnOrders.js'
import {productSelector} from '../../../../data/products.js'
import {formatDeclaredValueProperty} from '../../../LabelConfig/Fields/DeclaredValue.js'
import {lbToOz} from '../../../../common/weight.js'

export const MODAL_FORM = 'multibox_modal_form'

export function showMultiboxModal(labelInfoID, shipperType) {
  const savedConfig = cloneDeep(labelConfigSelector(getState(), {labelInfoID}))

  setForm(MODAL_FORM, {
    labelInfoID,
    shipperType,
    savedConfig,
    isSaving: false,
    hasInputError: false,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  const forms = formsSelector(state)

  return forms[MODAL_FORM]
}

async function confirmMultiboxModal() {
  const {labelInfoID, savedConfig} = modalFormSelector(getState())
  const labelConfig = labelConfigSelector(getState(), {labelInfoID})

  closeModal()

  if (!isEqual(savedConfig, labelConfig)) {
    showMessageToast('Multi-Package Labels were successfully updated.')

    await getRates([labelInfoID])
  }
}

export async function cancelMultiboxModal() {
  const form = modalFormSelector(getState())

  // already gone
  if (!form) {
    return
  }

  const {labelInfoID, savedConfig} = form

  try {
    closeModal()

    const labelConfig = labelConfigSelector(getState(), {labelInfoID})

    if (!isEqual(labelConfig.packages, savedConfig.packages)) {
      updateLabelConfig(labelInfoID, {packages: savedConfig.packages})

      await getRates([labelInfoID])
    }
  } catch (err) {
    updateLabelConfig(labelInfoID, {
      error_message: err.error_message || err.message,
    })
  }
}

function linesSelector(state) {
  const {labelInfoID} = modalFormSelector(state)
  const orderNumber = orderNumberFromLabelConfigSelector(state, {
    labelInfoID,
  })
  const referenceID = referenceIDFromLabelConfigSelector(state, {
    labelInfoID,
  })

  return orderNumber
    ? orderLinesSelector(state, {orderNumber})
    : referenceID
      ? returnOrderLinesSelector(state, {referenceID})
      : []
}

async function splitOrderInToPackages() {
  const {labelInfoID, shipperType} = modalFormSelector(getState())
  const maxLines = getMaxParcelCount(shipperType)
  const [firstPackage] = packagesSelector(getState(), {labelInfoID})
  const lines = linesSelector(getState())

  const packages = []

  outer: for (const line of lines) {
    const product = productSelector(getState(), {sku: line.sku})

    const productPackage = {
      ...firstPackage,
      weight: lbToOz(product.weight),
      length: product.length,
      height: product.height,
      width: product.width,
      ...VALID_DECLARED_VALUE_SHIPPER_TYPES.reduce((prev, shipperType) => {
        prev[formatDeclaredValueProperty(shipperType)] = product.price

        return prev
      }, {}),
    }

    for (let n = 0; n < line.quantity; n++) {
      if (packages.length === maxLines) {
        break outer
      }

      packages.push({...productPackage})
    }
  }

  updateLabelConfig(labelInfoID, {packages})

  await getRates([labelInfoID])
}

function onChange() {
  // noop
}

function MultiboxModal({form}) {
  const {labelInfoID, shipperType} = form

  const shipperTypes = useSelector((state) =>
    multiboxShipperTypesSelector(state, {
      labelInfoID,
    }),
  )
  const shipperTypeOptions = useMemo(
    () => SHIPPER_OPTIONS.filter(({value}) => shipperTypes.includes(value)),
    [],
  )
  const labelShipperNameSelector = useMemo(makeLabelShipperNameSelector, [])
  const boxShapeNameSelector = useMemo(makeBoxShapeNameSelector, [])

  const labelConfig = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )
  const orderNumber = useSelector((state) =>
    orderNumberFromLabelConfigSelector(state, {labelInfoID}),
  )
  const referenceID = useSelector((state) =>
    referenceIDFromLabelConfigSelector(state, {labelInfoID}),
  )
  const shipperName = useSelector((state) =>
    labelShipperNameSelector(state, {labelInfoID}),
  )
  const shippingMethod = useSelector((state) =>
    labelShippingMethodNameSelector(state, {labelInfoID}),
  )
  const boxShapeName = useSelector((state) =>
    boxShapeNameSelector(state, {
      labelInfoID,
      shipperType,
      packagesIndex: 0,
    }),
  )
  const createLabelImageDisabled = useSelector((state) =>
    createLabelImageDisabledSelector(state, {
      labelInfoID,
    }),
  )
  const cost = useSelector((state) =>
    selectedShippingRateCostSelector(state, {labelInfoID}),
  )
  const ratesLoading = useSelector((state) =>
    ratesLoadingSelector(state, {labelInfoID}),
  )
  const errors = useSelector((state) =>
    labelErrorsSelector(state, {labelInfoID, shipperType}),
  )
  const canAddMoreParcels = useSelector((state) => {
    const shipperTypes = shipperTypesSelector(state, {
      labelInfoID,
    })

    return (
      shipperTypes.filter((shipperType) =>
        canAddMoreParcelsSelector(state, {labelInfoID, shipperType}),
      ).length === shipperTypes.length
    )
  })
  const productCount = useSelector(linesSelector).reduce(
    (prev, line) => prev + line.quantity,
    0,
  )

  const showSubPackageType = shipperType === FEDEX

  return (
    <ConfirmModal
      title="Configure Multi-Package Label"
      modalSize="x-lg"
      onConfirm={() => confirmMultiboxModal()}
      onCancel={() => cancelMultiboxModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={form.hasInputError}
      error={form.serverError}
    >
      <div className="fs-01">
        {orderNumber ? (
          <>
            <strong>Order ID:</strong> <span>{orderNumber}</span>
          </>
        ) : referenceID ? (
          <>
            <strong>RMA ID:</strong> <span>{referenceID}</span>
          </>
        ) : null}
      </div>
      {shipperTypeOptions.length > 1 && (
        <div className="fs-00 text--md-grey w-10 margin-top-15 margin-bottom-10">
          <Zelect
            label="Carrier"
            value={shipperType}
            options={shipperTypeOptions}
            onChange={(option) => updateModalForm({shipperType: option.value})}
          />
        </div>
      )}
      <div className="fs-00 text--md-grey margin-bottom-20">
        <div>
          <span>{shipperName}, </span>
          <span>{boxShapeName}, </span>
          <span>{shippingMethod} – </span>
          {ratesLoading ? (
            <div className="spinning--sm spin--animation" />
          ) : (
            (createLabelImageDisabled && 'N/A') ||
            (isTBD(cost) && 'TBD') || (
              <strong className="success-txt">
                <Currency value={cost} />
              </strong>
            )
          )}
        </div>
        {errors.length > 0 && (
          <div
            className={className`wrap--error-wb lh-sm ${{
              'op-50': ratesLoading,
            }}`}
          >
            {errors.map((err, i) => (
              <small className="error margin-top-1" key={i}>
                {err}
              </small>
            ))}
          </div>
        )}
        <div className="margin-top-7">
          <ButtonRefresh
            title="Refresh rates"
            className="margin-right-10"
            loading={ratesLoading}
            onClick={() => getRates([labelInfoID])}
          />
          {productCount > 0 && (
            <ButtonPrimary
              isOutlined
              size="x-sm"
              onClick={() => splitOrderInToPackages()}
            >
              Split Items into Individual Packages
            </ButtonPrimary>
          )}
        </div>
      </div>

      <table className="table margin-bottom-0">
        <thead>
          <tr>
            <th className="table__th table__th--sm divider--right w-2">
              &nbsp;
            </th>
            <th className="table__th table__th--sm w-20">Weight</th>
            <th className="table__th table__th--sm w-25">Dimensions</th>
            <th className="table__th table__th--sm w-15">Dry Ice Weight</th>
            <th className="table__th table__th--sm w-15">Declared Value</th>
            {shipperType === UPS && (
              <th className="table__th table__th--sm w-15">Misc</th>
            )}
            {showSubPackageType && (
              <th className="table__th table__th--sm w-15">Sub-Package Type</th>
            )}
            <th className="table__th table__th--sm w-2">&nbsp;</th>
          </tr>
        </thead>
        <tbody className="table__tbody table__tbody--lines">
          {labelConfig.packages.map((parcel, index) => (
            <Row
              key={index}
              labelInfoID={form.labelInfoID}
              shipperType={form.shipperType}
              packagesIndex={index}
              onChange={onChange}
              showSubPackageType={showSubPackageType}
              isDisabled={ratesLoading}
            />
          ))}
        </tbody>
      </table>
      <div className="divider--top dark margin-top-0 align-center">
        <button
          className="btn btn--link fs-n0"
          onClick={() => addParcel(form.labelInfoID, onChange)}
          disabled={!canAddMoreParcels || ratesLoading}
        >
          + Add another package
        </button>
      </div>
    </ConfirmModal>
  )
}

export default onlyIfForm(MultiboxModal, modalFormSelector)
